import { SkySkeleton, SkySkeletonItem } from "@skypointcloud/skypoint-ui";

/**
 * What a page looks like while its code or first data is loading: the same
 * frame as a real page (title, description, a row of tiles, a table), drawn
 * as grey bars. A lone spinner on a blank canvas read as "the app is broken";
 * a page-shaped placeholder reads as "the page is arriving".
 *
 * `variant="list"` (default) fits list pages; `"form"` fits settings/detail
 * pages (stacked field rows instead of tiles + table).
 *
 * `panel` draws only the body — no page gutter, no title bars — for content
 * loading inside a page that already rendered its own frame and header (a tab
 * panel, a section).
 */
interface PageSkeletonProps {
  variant?: "list" | "form";
  label?: string;
  panel?: boolean;
}

export const PageSkeleton = ({
  variant = "list",
  label = "Loading page",
  panel = false,
}: PageSkeletonProps) => (
  <div
    className={panel ? undefined : "sky-page"}
    role="status"
    aria-live="polite"
    aria-label={label}
  >
    <SkySkeleton className="flex flex-col gap-6">
      {!panel && (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <SkySkeletonItem className="h-8! w-56!" />
            <SkySkeletonItem className="h-4! w-80! max-w-[70vw]" />
          </div>
          <SkySkeletonItem className="h-9! w-36! rounded-md!" />
        </div>
      )}

      {variant === "list" ? (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5"
              >
                <SkySkeletonItem className="h-4! w-24!" />
                <SkySkeletonItem className="h-7! w-16!" />
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5">
            <SkySkeletonItem className="h-5! w-full!" />
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <SkySkeletonItem
                key={i}
                className="h-9! w-full!"
              />
            ))}
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-5 rounded-xl border border-border bg-card p-5">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            >
              <div className="flex flex-col gap-1.5">
                <SkySkeletonItem className="h-4! w-28!" />
                <SkySkeletonItem className="h-9! w-full!" />
              </div>
              <div className="flex flex-col gap-1.5">
                <SkySkeletonItem className="h-4! w-28!" />
                <SkySkeletonItem className="h-9! w-full!" />
              </div>
            </div>
          ))}
        </div>
      )}
    </SkySkeleton>
  </div>
);

export default PageSkeleton;
